import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Chip, CircularProgress, IconButton, InputAdornment, MenuItem, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, Alert,
} from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { AdminPageHeader, errorMessage } from '../../components/Admin/adminUi';
import { KIND_LABELS, KindChip, PriorityChip, TicketStatusChip, relativeTime } from '../../components/Support/supportUi';

const STATUS_FILTERS = [
  { value: 'new', label: 'New', color: 'info' },
  { value: 'open', label: 'Open', color: 'primary' },
  { value: 'waiting', label: 'Waiting', color: 'warning' },
  { value: 'resolved', label: 'Resolved', color: 'success' },
  { value: 'closed', label: 'Closed', color: 'default' },
  { value: 'all', label: 'All', color: 'default' },
];
const DEFAULT_KINDS = ['support', 'partnership', 'sales', 'bug', 'feature'];

const AdminSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [kinds, setKinds] = useState(DEFAULT_KINDS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('all');
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  // Debounce the search box into the server query.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { status, kind };
      if (q) params.q = q;
      const res = await apiService.admin.getSupportTickets(params);
      setTickets(res.data.tickets || []);
      setCounts(res.data.counts || {});
      if (Array.isArray(res.data.kinds) && res.data.kinds.length) setKinds(res.data.kinds);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load support tickets'));
    } finally {
      setLoading(false);
    }
  }, [status, kind, q]);

  useEffect(() => { load(); }, [load]);

  const total = Object.values(counts).reduce((sum, n) => sum + (Number(n) || 0), 0);
  const activeCount = (Number(counts.new) || 0) + (Number(counts.open) || 0);

  return (
    <Box>
      <AdminPageHeader
        title="Support"
        subtitle={`${activeCount} ticket${activeCount === 1 ? '' : 's'} needing attention · support, partnership and sales requests`}
        actions={<Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>}
      />
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {STATUS_FILTERS.map((s) => {
              const n = s.value === 'all' ? total : Number(counts[s.value]) || 0;
              const selected = status === s.value;
              return (
                <Chip
                  key={s.value}
                  label={`${s.label} (${n})`}
                  color={s.color}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => setStatus(s.value)}
                  sx={{ fontWeight: selected ? 700 : 400 }}
                />
              );
            })}
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              fullWidth size="small" placeholder="Search number, subject, requester, email or company"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <TextField select size="small" label="Kind" value={kind} onChange={(e) => setKind(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="all">All kinds</MenuItem>
              {kinds.map((k) => <MenuItem key={k} value={k}>{KIND_LABELS[k] || k}</MenuItem>)}
            </TextField>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Number</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Requester</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Last activity</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && tickets.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center"><CircularProgress size={28} sx={{ my: 3 }} /></TableCell></TableRow>
                ) : tickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>No tickets match the current filters</Typography>
                    </TableCell>
                  </TableRow>
                ) : tickets.map((t) => (
                  <TableRow key={t._id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/support/${t._id}`)}>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{t.number}</TableCell>
                    <TableCell><KindChip kind={t.kind} /></TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" fontWeight={t.status === 'new' ? 700 : 500} noWrap title={t.subject}>{t.subject}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.messageCount ?? t.messages?.length ?? 0} message{(t.messageCount ?? t.messages?.length ?? 0) === 1 ? '' : 's'}
                        {t.lastMessageFrom === 'requester' && !['resolved', 'closed'].includes(t.status) ? ' · awaiting our reply' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.requester?.name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{t.requester?.email}</Typography>
                      {t.requester?.company && <Typography variant="caption" color="text.secondary">{t.requester.company}</Typography>}
                    </TableCell>
                    <TableCell><TicketStatusChip status={t.status} /></TableCell>
                    <TableCell><PriorityChip priority={t.priority} /></TableCell>
                    <TableCell><Typography variant="body2" color={t.assignee ? 'text.primary' : 'text.secondary'}>{t.assignee || 'Unassigned'}</Typography></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={t.lastMessageAt ? new Date(t.lastMessageAt).toLocaleString() : ''}>
                        <span>{relativeTime(t.lastMessageAt || t.createdAt)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={t.source === 'app' ? 'App' : 'Web'} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Showing {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminSupport;
